import React, { useState, useRef, useEffect } from 'react';
import { useCSVStore, UploadedFile } from '../../stores/csvStore';
import { CSVFile, Location, DetectedFormat } from '../../types/data/csv';
import ColumnConflictResolver from './ColumnConflictResolver';
import './FileUploader.css';
import csvService from '../../services/csvService';

interface FileUploaderProps {
  onFileProcessed: () => void;
  onFileUploaded?: (fileRef: string) => void;
  skipValidation?: boolean;
}

export const FileUploader: React.FC<FileUploaderProps> = ({ onFileProcessed, onFileUploaded, skipValidation = false }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { 
    setCSVData, 
    setIsLoading, 
    isLoading, 
    setError, 
    uploadedFile,
    setUploadedFile,
    // Multi-file support
    uploadedFiles,
    selectedFileId,
    addUploadedFile,
    removeUploadedFile,
    setSelectedFileId,
    columnConflicts,
    setColumnConflicts,
    getAllUniqueColumns,
    getFilesContainingColumn,
    accessibleLocations,
    selectedLocation,
    setSelectedLocation,
    setAccessibleLocations,
    isMultiLocationMode,
    setMultiLocationMode
  } = useCSVStore();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [detectedFormat, setDetectedFormat] = useState<DetectedFormat | null>(null);
  const [showConflictResolver, setShowConflictResolver] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

  // Fetch accessible locations on component mount
  useEffect(() => {
    fetchAccessibleLocations();
  }, []);

  // Update primary file when uploadedFiles change
  useEffect(() => {
    if (uploadedFiles.length > 0 && !uploadedFile) {
      const primaryFile = uploadedFiles[0];
      setUploadedFile(primaryFile.csvFile);
      setCSVData([primaryFile.parsedData.headers, ...primaryFile.parsedData.rows]);
      console.log('✅ Set primary file for workflow:', primaryFile.name);
    }
  }, [uploadedFiles, uploadedFile, setUploadedFile, setCSVData]);

  const fetchAccessibleLocations = async () => {
    try {
      setLocationError(null);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/accessible-locations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch accessible locations: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setAccessibleLocations(data.data);
      }
    } catch (error) {
      console.error('Error fetching accessible locations:', error);
      setLocationError('Failed to load accessible locations');
      setAccessibleLocations([]);
    }
  };

  const createCSVFile = (data: string[][], fileName: string): CSVFile => {
    const headers = data[0] || [];
    const rows = data.slice(1);
    
    // Create a CSV file object
    return {
      id: `file_${Date.now()}`,
      name: fileName,
      size: JSON.stringify(data).length,
      fileType: 'csv',
      lastModified: Date.now(),
      uploadDate: Date.now(),
      columns: headers,
      rows: rows,
      previewData: rows.slice(0, 10),
      data: data // Include the full data array
    };
  };

  const handleFileChange = async (files: FileList) => {
    if (!files || files.length === 0) return;
    
    // Clear previous errors
    setError(null);
    setErrorMessage(null);
    
    setIsLoading(true);
    
    try {
      // Determine if this should be single-file or multi-file processing
      if (files.length === 1) {
        // Single file - use legacy single file processing for proper preview
        console.log('🔄 Processing single file via single-file pipeline');
        await handleSingleFileChange(files[0]);
      } else {
        // Multiple files - use multi-file processing
        console.log(`🔄 Processing ${files.length} files via multi-file pipeline`);
        
        // Process each file
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          
          // Validate file type - only allow CSV files
          const fileExt = file.name.split('.').pop()?.toLowerCase();
          const supportedExts = ['csv'];
          
          if (!fileExt || !supportedExts.includes(fileExt)) {
            const errorMsg = `Unsupported file type for ${file.name}. Please upload CSV files only.`;
            setError(errorMsg);
            setErrorMessage(errorMsg);
            continue;
          }

          await processCSVFileForMultiUpload(file);
        }
      }
    } catch (error) {
      console.error('Error processing files:', error);
      const errorMsg = 'Error processing files: ' + (error instanceof Error ? error.message : String(error));
      setError(errorMsg);
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
      // Trigger callback to let parent know files are processed
      onFileProcessed();
    }
  };

  // Single file handler for proper single-file processing
  const handleSingleFileChange = async (file: File) => {
    if (!file) return;
    
    // Validate file type - only allow CSV files
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const supportedExts = ['csv'];
    
    if (!fileExt || !supportedExts.includes(fileExt)) {
      const errorMsg = `Unsupported file type. Please upload a CSV file only.`;
      setError(errorMsg);
      setErrorMessage(errorMsg);
      return; // Don't throw, just return to let parent handle the error state
    }

    console.log('🔄 Processing single file:', file.name);
    // Parse CSV first, inject dealer column, then upload the modified file
    await processCSVFile(file);
  };

  // New function to process CSV files for multi-file upload
  const processCSVFileForMultiUpload = async (file: File) => {
    try {
      console.log(`🔄 Processing file for multi-upload: ${file.name}`);
      
      // Use csvService to parse the CSV
      const csvFile = await csvService.parseCSV(file);
      
      console.log(`📊 Parsed ${file.name}:`, {
        columns: csvFile.columns.length,
        rows: csvFile.rows.length,
        headers: csvFile.columns
      });
      
      // Create UploadedFile object
      const uploadedFile: UploadedFile = {
        id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        file: file,
        name: file.name,
        parsedData: {
          headers: csvFile.columns,
          rows: csvFile.rows
        },
        isValid: true, // We'll validate later
        csvFile: csvFile
      };
      
      // Add to store
      addUploadedFile(uploadedFile);
      
      console.log(`✅ Successfully added ${file.name} to multi-file store`);
      
    } catch (error) {
      console.error(`❌ Error processing ${file.name}:`, error);
      const errorMsg = `Error processing ${file.name}: ${error instanceof Error ? error.message : String(error)}`;
      setError(errorMsg);
      setErrorMessage(errorMsg);
    }
  };

  // Function to inject dealer column into CSV data (single location mode) or validate locations (multi-location mode)
  const injectDealerColumn = async (csvData: string[][]): Promise<string[][]> => {
    console.log('🔍 DEBUG DEALER PROCESSING: Starting dealer processing');
    console.log('🔍 DEBUG DEALER PROCESSING: Multi-location mode:', isMultiLocationMode);
    console.log('🔍 DEBUG DEALER PROCESSING: Original CSV data:', csvData);
    console.log('🔍 DEBUG DEALER PROCESSING: Original headers:', csvData[0]);
    
    try {
      if (isMultiLocationMode) {
        // Multi-location mode: validate that CSV has dealer column and all dealers are accessible
        return await validateMultiLocationCSV(csvData);
      } else {
        // Single location mode: inject dealer column as before
        return await injectSingleLocationDealer(csvData);
      }
    } catch (error) {
      console.error('🔍 DEBUG DEALER PROCESSING: Error occurred:', error);
      throw error; // Re-throw to show error to user
    }
  };

  // Validate multi-location CSV data and add location ID mapping
  const validateMultiLocationCSV = async (csvData: string[][]): Promise<string[][]> => {
    console.log('🔍 DEBUG MULTI-LOCATION: Validating multi-location CSV');
    
    const [headers, ...rows] = csvData;
    
    // Find dealer column index
    const dealerColumnIndex = headers.findIndex(header => 
      header.toLowerCase().trim() === 'dealer' || 
      header.toLowerCase().trim() === 'dealer name' ||
      header.toLowerCase().trim() === 'location'
    );
    
    if (dealerColumnIndex === -1) {
      throw new Error('Multi-location mode requires a "Dealer" column in your CSV file. Please add a column with dealer/location names.');
    }
    
    // Create a mapping from dealer names to location IDs
    const dealerNameToLocationId = new Map<string, number>();
    accessibleLocations.forEach(location => {
      dealerNameToLocationId.set(location.name.toLowerCase().trim(), location.id);
    });
    
    console.log('🔍 DEBUG MULTI-LOCATION: Dealer name to location ID mapping:', Array.from(dealerNameToLocationId.entries()));
    
    // Extract unique dealer names from CSV and validate them
    const dealerNamesInCSV = new Set<string>();
    const invalidDealers: string[] = [];
    const validDealers: string[] = [];
    
    rows.forEach(row => {
      const dealerName = row[dealerColumnIndex]?.trim();
      if (dealerName) {
        dealerNamesInCSV.add(dealerName);
        const normalizedDealerName = dealerName.toLowerCase().trim();
        if (dealerNameToLocationId.has(normalizedDealerName)) {
          if (!validDealers.includes(dealerName)) {
            validDealers.push(dealerName);
          }
        } else {
          if (!invalidDealers.includes(dealerName)) {
            invalidDealers.push(dealerName);
          }
        }
      }
    });
    
    console.log('🔍 DEBUG MULTI-LOCATION: Dealers found in CSV:', Array.from(dealerNamesInCSV));
    
    if (invalidDealers.length > 0) {
      throw new Error(`You don't have access to the following locations found in your CSV: ${invalidDealers.join(', ')}. Please remove these rows or contact your administrator.`);
    }
    
    console.log('🔍 DEBUG MULTI-LOCATION: All dealers validated successfully:', validDealers);
    
    // Add LocationID column to CSV headers (after Dealer column)
    const newHeaders = [...headers];
    const locationIdColumnIndex = dealerColumnIndex + 1;
    newHeaders.splice(locationIdColumnIndex, 0, 'LocationID');
    
    // Add location IDs to each row based on dealer name
    const newRows = rows.map(row => {
      const dealerName = row[dealerColumnIndex]?.trim();
      const locationId = dealerName ? dealerNameToLocationId.get(dealerName.toLowerCase().trim()) : undefined;
      
      const newRow = [...row];
      newRow.splice(locationIdColumnIndex, 0, locationId ? locationId.toString() : '');
      
      return newRow;
    });
    
    console.log('🔍 DEBUG MULTI-LOCATION: Added LocationID column at index:', locationIdColumnIndex);
    console.log('🔍 DEBUG MULTI-LOCATION: New headers:', newHeaders);
    console.log('🔍 DEBUG MULTI-LOCATION: Sample row with LocationID:', newRows[0]);
    
    return [newHeaders, ...newRows];
  };

  // Inject dealer column for single location mode
  const injectSingleLocationDealer = async (csvData: string[][]): Promise<string[][]> => {
    console.log('🔍 DEBUG SINGLE-LOCATION: Injecting dealer for single location');
    
    if (!selectedLocation) {
      throw new Error('No location selected. Please select a location before uploading.');
    }
    
    const dealerName = selectedLocation.name;
    console.log('🔍 DEBUG SINGLE-LOCATION: Dealer name to inject:', dealerName);
    
    // Inject dealer column into CSV data
    const [headers, ...rows] = csvData;
    console.log('🔍 DEBUG SINGLE-LOCATION: Separated headers:', headers);
    console.log('🔍 DEBUG SINGLE-LOCATION: Separated rows count:', rows.length);
    
    // Add "Dealer" as the second column (after Brand)
    const newHeaders = [headers[0], 'Dealer', ...headers.slice(1)];
    const newRows = rows.map(row => [row[0], dealerName, ...row.slice(1)]);
    
    console.log('🔍 DEBUG SINGLE-LOCATION: New headers:', newHeaders);
    console.log('🔍 DEBUG SINGLE-LOCATION: New rows sample:', newRows.slice(0, 2));
    console.log('🔍 DEBUG SINGLE-LOCATION: Injection completed successfully');
    
    return [newHeaders, ...newRows];
  };

  // Function to upload modified CSV (with dealer column) to backend
  const uploadModifiedCSVToBackend = async (csvData: string[][], originalFileName: string, detectedFormat?: DetectedFormat) => {
    try {
      console.log('🔍 DEBUG UPLOAD: Starting upload of modified CSV to backend');
      
      // Use the original delimiter from detection, or default to comma
      const delimiter = detectedFormat?.delimiter || ',';
      
      // Convert CSV data back to string format using the detected delimiter
      const csvContent = csvData.map(row => row.join(delimiter)).join('\n');
      console.log('🔍 DEBUG UPLOAD: Using delimiter:', delimiter);
      console.log('🔍 DEBUG UPLOAD: CSV content to upload:', csvContent.substring(0, 200) + '...');
      
      // Create a new File object with the modified CSV content
      const modifiedFile = new File([csvContent], originalFileName, { type: 'text/csv' });
      
      const formData = new FormData();
      formData.append('file', modifiedFile);
      
      console.log('🔍 DEBUG UPLOAD: Sending modified CSV to backend...');
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData
      });
      
      console.log('🔍 DEBUG UPLOAD: Backend response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('🔍 DEBUG UPLOAD: Backend error:', errorData);
        throw new Error(errorData.error || 'File upload failed');
      }
      
      const data = await response.json();
      console.log('🔍 DEBUG UPLOAD: Backend response data:', data);
      
      if (data.fileRef && typeof data.fileRef === 'string') {
        if (typeof onFileUploaded === 'function') {
          onFileUploaded(data.fileRef);
        }
      }
      
      console.log('🔍 DEBUG UPLOAD: Upload completed successfully');
    } catch (error) {
      console.error('🔍 DEBUG UPLOAD: Upload failed:', error);
      setError('Upload failed: ' + (error instanceof Error ? error.message : String(error)));
      throw error;
    }
  };

  const processCSVFile = async (file: File) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      setDetectedFormat(null);
      
      // Clear any previous errors when starting processing
      if (skipValidation) {
        setError(null);
      }
      
      // Use csvService with csv-sniffer for parsing
      const csvFile = await csvService.parseCSV(file);
      
      // Set detected format for preview
      if (csvFile.detectedFormat) {
        setDetectedFormat(csvFile.detectedFormat);
        console.log('🔍 Format Detection Results:', csvFile.detectedFormat);
      }
      
      // Convert csvFile data to the format expected by existing functions
      const csvData = [csvFile.columns, ...csvFile.rows];
      console.log('🔍 DEBUG CSV PARSER: Parsed CSV data:', csvData);
      console.log('🔍 DEBUG CSV PARSER: Headers:', csvFile.columns);
      console.log('🔍 DEBUG CSV PARSER: Data rows sample:', csvFile.rows.slice(0, 2));
      
      // Inject Dealer column BEFORE validation - fetch user's accessible location and add dealer column
      const csvDataWithDealer = await injectDealerColumn(csvData);
      console.log('🔍 DEBUG CSV PARSER: CSV data with dealer column:', csvDataWithDealer);
      console.log('🔍 DEBUG CSV PARSER: Headers after injection:', csvDataWithDealer[0]);
      
      // Create CSV file object with injected dealer column
      const csvFileWithDealer = createCSVFile(csvDataWithDealer, file.name);
      // Preserve the detected format
      csvFileWithDealer.detectedFormat = csvFile.detectedFormat;
      
      console.log('🔍 DEBUG CSV PARSER: Created CSV file object:', csvFileWithDealer);
      console.log('🔍 DEBUG CSV PARSER: CSV file columns:', csvFileWithDealer.columns);
      
      // Validate CSV headers only if validation is not skipped
      if (!skipValidation) {
        console.log('🔍 DEBUG CSV PARSER: About to validate CSV with headers:', csvFileWithDealer.columns);
        const validationResult = csvService.validateCSVForFinancials(csvFileWithDealer);
        console.log('🔍 DEBUG CSV VALIDATION: Validation result:', validationResult);
        
        if (!validationResult.isValid) {
          // Show validation errors
          const errorMessages = validationResult.errors.map(error => error.message).join('; ');
          const errorMsg = `CSV validation failed: ${errorMessages}`;
          setError(errorMsg);
          setErrorMessage(errorMsg);
          setIsLoading(false);
          return;
        }
      } else {
        console.log('🔍 DEBUG CSV PARSER: Skipping validation for Submit Data page');
        // Clear any previous validation errors when skipping validation
        setError(null);
        setErrorMessage(null);
      }
      
      setCSVData(csvDataWithDealer);
      setUploadedFile(csvFileWithDealer);
      
      // Now upload the CSV with dealer column injected to the backend
      await uploadModifiedCSVToBackend(csvDataWithDealer, file.name, csvFile.detectedFormat);
      
      setIsLoading(false);
      onFileProcessed();
    } catch (error) {
      console.error('Error parsing file:', error);
      const errorMsg = 'Error parsing CSV file: ' + (error instanceof Error ? error.message : String(error));
      setError(errorMsg);
      setErrorMessage(errorMsg);
      setIsLoading(false);
    }
  };

  // Simple CSV line parser
  const parseCsvLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"' && (i === 0 || line[i-1] !== '\\')) {
        inQuotes = !inQuotes;
        continue;
      }
      
      if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
        continue;
      }
      
      current += char;
    }
    
    // Add the last field
    result.push(current.trim());
    
    return result;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFileChange(e.dataTransfer.files);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadExample = async () => {
    try {
      // Fetch the example file from the public directory or server
      const response = await fetch('/enriched_kpi_test.csv');
      if (!response.ok) {
        throw new Error('Failed to fetch example file');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'enriched_kpi_test.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading example file:', error);
      setErrorMessage('Failed to download example file');
    }
  };

  const handleConflictResolution = (resolutions: { [columnName: string]: string }) => {
    console.log('🔧 Conflict resolutions:', resolutions);
    
    // Clear the conflicts since they're now resolved
    setColumnConflicts([]);
    
    // Create a consolidated CSV file for the workflow
    // For now, use the first uploaded file as the primary file
    if (uploadedFiles.length > 0) {
      const primaryFile = uploadedFiles[0];
      setUploadedFile(primaryFile.csvFile);
      setCSVData([primaryFile.parsedData.headers, ...primaryFile.parsedData.rows]);
    }
    
    console.log('✅ Conflicts resolved, ready for mapping');
  };


  return (
    <div className="file-uploader-container">
      <h2 className="uploader-title">Upload CSV File</h2>
      
      {/* Location Selection */}
      {locationError && (
        <div className="error-message">
          {locationError}
        </div>
      )}
      
      {accessibleLocations.length > 1 && (
        <div className="location-selection-container">
          <h3 className="location-selection-title">
            Select Location
          </h3>
          <p className="location-selection-description">
            You have access to {accessibleLocations.length} locations. Please select which location this data belongs to:
          </p>
          <select
            className="location-select"
            value={isMultiLocationMode ? 'multiple' : (selectedLocation?.id || '')}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'multiple') {
                setMultiLocationMode(true);
                setSelectedLocation(null);
              } else {
                setMultiLocationMode(false);
                const locationId = parseInt(value);
                const location = accessibleLocations.find(loc => loc.id === locationId);
                setSelectedLocation(location || null);
              }
            }}
          >
            <option value="">-- Select a location --</option>
            <option value="multiple">📍 Multiple locations (include in CSV)</option>
            <option disabled>── Single Locations ──</option>
            {accessibleLocations.map(location => (
              <option key={location.id} value={location.id}>
                {location.name} {location.code ? `(${location.code})` : ''}
              </option>
            ))}
          </select>
          {isMultiLocationMode ? (
            <div className="multi-location-info">
              <p className="multi-location-description">
                📋 <strong>Multi-location mode:</strong> Your CSV file must include a "Dealer" column with location names.
                We'll validate that all locations in your file are ones you have access to.
              </p>
              <p className="multi-location-example">
                <strong>Example:</strong> Make sure your CSV has columns like: Brand, Dealer, Year, Month, KPI Code, Value
              </p>
            </div>
          ) : !selectedLocation && accessibleLocations.length > 1 && (
            <p className="location-selection-warning">
              Please select a location or choose "Multiple locations" before uploading your file.
            </p>
          )}
        </div>
      )}
      
      {accessibleLocations.length === 1 && selectedLocation && (
        <div className="location-info-container">
          <p className="location-info-text">
            ✓ Uploading to: <strong>{selectedLocation.name}</strong>
            {selectedLocation.code && ` (${selectedLocation.code})`}
          </p>
        </div>
      )}
      
      <div
        className={`upload-dropzone ${isDragging ? 'dragging' : ''} ${accessibleLocations.length > 1 && !selectedLocation && !isMultiLocationMode ? 'disabled' : ''}`}
        onClick={accessibleLocations.length > 1 && !selectedLocation && !isMultiLocationMode ? undefined : handleClick}
        onDragOver={accessibleLocations.length > 1 && !selectedLocation && !isMultiLocationMode ? undefined : handleDragOver}
        onDragLeave={accessibleLocations.length > 1 && !selectedLocation && !isMultiLocationMode ? undefined : handleDragLeave}
        onDrop={accessibleLocations.length > 1 && !selectedLocation && !isMultiLocationMode ? undefined : handleDrop}
        style={{
          opacity: accessibleLocations.length > 1 && !selectedLocation && !isMultiLocationMode ? 0.5 : 1,
          cursor: accessibleLocations.length > 1 && !selectedLocation && !isMultiLocationMode ? 'not-allowed' : 'pointer'
        }}
      >
        {isLoading ? (
          <div className="loading-spinner"></div>
        ) : (
          <div className="upload-content">
            <div className="upload-icon"></div>
            <h3>Drag & Drop your CSV files here</h3>
            <p>or click to browse files (multiple files supported)</p>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".csv"
              multiple
              onChange={async (e) => {
                if (e.target.files && e.target.files.length > 0) {
                  await handleFileChange(e.target.files);
                }
              }}
            />
          </div>
        )}
      </div>
      
      {/* Multi-File Display */}
      {uploadedFiles.length > 0 && (
        <div className="uploaded-files-section">
          <h3>Uploaded Files ({uploadedFiles.length})</h3>
          <div className="uploaded-files-list">
            {uploadedFiles.map(file => (
              <div key={file.id} className={`uploaded-file-item ${selectedFileId === file.id ? 'selected' : ''}`}>
                <div className="file-info">
                  <div className="file-name">{file.name}</div>
                  <div className="file-details">
                    {file.parsedData.headers.length} columns, {file.parsedData.rows.length} rows
                  </div>
                </div>
                <div className="file-actions">
                  <button 
                    className="preview-btn"
                    onClick={() => setSelectedFileId(file.id)}
                    disabled={selectedFileId === file.id}
                  >
                    {selectedFileId === file.id ? 'Previewing' : 'Preview'}
                  </button>
                  <button 
                    className="remove-btn"
                    onClick={() => removeUploadedFile(file.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Column Conflicts Warning */}
          {columnConflicts.length > 0 && (
            <div className="column-conflicts-warning">
              <h4>⚠️ Duplicate Column Names Detected</h4>
              <p>The following columns exist in multiple files. You'll need to choose which file to use for each column during mapping:</p>
              <ul>
                {columnConflicts.map(conflict => (
                  <li key={conflict.columnName}>
                    <strong>"{conflict.columnName}"</strong> found in: {conflict.files.map(f => f.fileName).join(', ')}
                  </li>
                ))}
              </ul>
              <div className="conflict-actions">
                <button 
                  className="resolve-conflicts-btn"
                  onClick={() => setShowConflictResolver(true)}
                >
                  🔧 Resolve Conflicts Now
                </button>
                <button 
                  className="skip-conflicts-btn"
                  onClick={() => handleConflictResolution({})}
                >
                  ⏭️ Skip for Now
                </button>
                <p className="conflict-resolution-note">
                  💡 You can resolve conflicts now or during the mapping step.
                </p>
              </div>
            </div>
          )}
          
          {/* Summary of Available Columns */}
          <div className="columns-summary">
            <h4>📊 Ready for Mapping</h4>
            <p>
              <strong>{getAllUniqueColumns().length} unique columns</strong> available from {uploadedFiles.length} files.
              {columnConflicts.length > 0 && (
                <span className="conflict-summary"> ({columnConflicts.length} conflicts to resolve)</span>
              )}
            </p>
            <p className="standard-columns-note">
              ℹ️ Standard columns (Dealer, Year, Month) are expected in all files and won't cause conflicts.
            </p>
          </div>
        </div>
      )}

      {errorMessage && !skipValidation && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}
      
      {/* Compact Format Detection Preview */}
      {detectedFormat && (
        <div className="detected-format-compact">
          <div className="format-compact-title">🔍 Format Detected:</div>
          <div className="format-compact-info">
            <span className="format-compact-item">
              {detectedFormat.delimiter === ',' && '🔸 Comma (Standard)'}
              {detectedFormat.delimiter === ';' && '🔸 Semicolon (European)'}
              {detectedFormat.delimiter === '\t' && '🔸 Tab-Separated'}
              {detectedFormat.delimiter === '|' && '🔸 Pipe-Delimited'}
              {![',', ';', '\t', '|'].includes(detectedFormat.delimiter) && `🔸 ${detectedFormat.delimiter}`}
            </span>
            <span className="format-compact-item">
              {detectedFormat.isEuropeanFormat ? '🌍 European Numbers' : '🇺🇸 Standard Numbers'}
            </span>
          </div>
          <div className="format-compact-note">
            📋 Full format details will be shown in the preview step
          </div>
        </div>
      )}
      
      <div className="file-actions">
        <p className="file-info-text">
          Supported file type: CSV (.csv) only
        </p>
        
        <div className="action-buttons">
          <button
            className="select-file-button"
            onClick={handleClick}
            disabled={isLoading || (accessibleLocations.length > 1 && !selectedLocation && !isMultiLocationMode)}
          >
            {isLoading ? 'Processing...' : 
             (accessibleLocations.length > 1 && !selectedLocation && !isMultiLocationMode) ? 'Select Location or Mode First' : 
             'Select CSV File'}
          </button>
          
          {!skipValidation && (
            <button
              className="download-example-button"
              onClick={handleDownloadExample}
              disabled={isLoading}
            >
              📥 Download Example File
            </button>
          )}
        </div>
      </div>

      {/* Column Conflict Resolver Modal */}
      <ColumnConflictResolver
        isOpen={showConflictResolver}
        onClose={() => setShowConflictResolver(false)}
        onResolveAll={handleConflictResolution}
      />
    </div>
  );
}; 